import { ClassificationMetric } from "../stats";
export var BoundType;
(function (BoundType) {
    BoundType["PercentHigher"] = "percent_higher";
    BoundType["PercentLower"] = "percent_lower";
    BoundType["Value"] = "value";
})(BoundType || (BoundType = {}));
export var AllocationMetric;
(function (AllocationMetric) {
    AllocationMetric["Profit"] = "profit";
    AllocationMetric["Units"] = "units";
    AllocationMetric["Cost"] = "cost";
})(AllocationMetric || (AllocationMetric = {}));
class OptimizationConstraint {
    isBelowMax;
    isAboveMin;
    isWithinBounds;
    constructor(isBelowMax, isAboveMin, isWithinBounds) {
        this.isBelowMax = isBelowMax;
        this.isAboveMin = isAboveMin;
        this.isWithinBounds = isWithinBounds;
    }
    static andAll(constraints) {
        if (constraints.length == 0) {
            return {
                isBelowMax: true,
                isAboveMin: true,
                isWithinBounds: true
            };
        }
        return {
            isBelowMax: constraints.some((c) => c.isBelowMax),
            isAboveMin: constraints.every((c) => c.isAboveMin),
            isWithinBounds: constraints.every((c) => c.isWithinBounds)
        };
    }
}
const NONE_CONSTRAINT = new OptimizationConstraint(true, true, true);
class NoneConstraintProvider {
    static INSTANCE = new NoneConstraintProvider();
    getConstraint(x) {
        return NONE_CONSTRAINT;
    }
    static getInstance() {
        return this.INSTANCE;
    }
}
class CompositeConstraintProvider {
    delegates;
    constructor(delegates) {
        this.delegates = delegates;
    }
    getConstraint(x) {
        let isBelowMax = this.delegates.length == 0;
        let isAboveMin = true;
        let isWithinBounds = true;
        let i = this.delegates.length;
        while (i--) {
            const constraint = this.delegates[i].getConstraint(x);
            isBelowMax = isBelowMax || constraint.isBelowMax;
            isAboveMin = isAboveMin && constraint.isAboveMin;
            isWithinBounds = isWithinBounds && constraint.isWithinBounds;
        }
        return {
            isBelowMax: isBelowMax,
            isAboveMin: isAboveMin,
            isWithinBounds: isWithinBounds
        };
    }
    static create(delegates) {
        switch (delegates.length) {
            case 0:
                return NoneConstraintProvider.getInstance();
            case 1:
                return delegates[0];
            default:
                return new CompositeConstraintProvider(delegates);
        }
    }
}
class SegmentConstraintProvider {
    min;
    max;
    accessor;
    constructor(min, max, accessor) {
        this.min = min;
        this.max = max;
        this.accessor = accessor;
    }
    getConstraint(point) {
        const value = this.accessor(point);
        const isBelowMax = value <= this.max;
        const isAboveMin = value >= this.min;
        return {
            isBelowMax: isBelowMax,
            isAboveMin: isAboveMin,
            isWithinBounds: isBelowMax && isAboveMin
        };
    }
    static createFromSegment(config) {
        const constraints = config.constraints || [];
        const providers = constraints.map((c) => SegmentConstraintProvider.create(c, config.assumptions));
        return CompositeConstraintProvider.create(providers);
    }
    static create(config, assumptions) {
        const min = config.min !== undefined && isFinite(config.min) ? config.min : -Infinity;
        const max = config.max !== undefined && isFinite(config.max) ? config.max : Infinity;
        if (min <= -Infinity && max >= Infinity) {
            return new NoneConstraintProvider();
        }
        const accessor = SegmentMetric.asFrontierPointAccessor(config.type, assumptions);
        return new SegmentConstraintProvider(min, max, accessor);
    }
}
class ProjectConstraintProvider {
    min;
    max;
    accessors;
    constructor(min, max, accessors) {
        this.min = min;
        this.max = max;
        this.accessors = accessors;
    }
    getConstraint(args) {
        let value = 0;
        let i = args[1].length;
        while (i--) {
            value += this.accessors[i](args[0][i].iterator.points[args[1][i]]);
        }
        const isBelowMax = value <= this.max;
        const isAboveMin = value >= this.min;
        return {
            isBelowMax: isBelowMax,
            isAboveMin: isAboveMin,
            isWithinBounds: isBelowMax && isAboveMin
        };
    }
    static create(config, segments) {
        const min = config.min !== undefined && isFinite(config.min) ? config.min : -Infinity;
        const max = config.max !== undefined && isFinite(config.max) ? config.max : Infinity;
        if (min <= -Infinity && max >= Infinity) {
            return new NoneConstraintProvider();
        }
        const accessors = segments.map((segments) => SegmentMetric.asFrontierPointAccessor(config.type, segments.getConfig().assumptions));
        return new ProjectConstraintProvider(min, max, accessors);
    }
}
class FrontierPoint {
    confusionMatrix;
    model;
    costMapper;
    valueMapper;
    cost;
    value;
    constructor(confusionMatrix, model, costMapper, valueMapper) {
        this.confusionMatrix = confusionMatrix;
        this.model = model;
        this.costMapper = costMapper;
        this.valueMapper = valueMapper;
        this.cost = costMapper(confusionMatrix);
        this.value = valueMapper(confusionMatrix);
    }
    getConfusionMatrix() {
        return this.confusionMatrix;
    }
    copyAndAdjustCapacity(capacity) {
        const adjustedConfusionMatrix = this.model.confusionMatrixFactory.getConfusionMatrix({ capacity });
        return new FrontierPoint(adjustedConfusionMatrix, this.model, this.costMapper, this.valueMapper);
    }
    static create(confusionMatrix, model, costMapper, valueMapper) {
        return new FrontierPoint(confusionMatrix, model, costMapper, valueMapper);
    }
}
class FrontierPointIterator {
    points;
    maxIndex;
    currentIndex;
    current;
    next;
    hasNext;
    nextMaxes;
    nextMaxValue;
    constructor(points, currentIndex, current, next, hasNext, nextMaxes, nextMaxValue) {
        this.points = points;
        this.maxIndex = points.length - 1;
        this.currentIndex = currentIndex;
        this.current = current;
        this.next = next;
        this.hasNext = hasNext;
        this.nextMaxes = nextMaxes;
        this.nextMaxValue = nextMaxValue;
    }
    advance() {
        return this.advanceToIndex(this.currentIndex + 1);
    }
    advanceToIndex(index) {
        if (index >= this.points.length) {
            throw new Error("Index out of bounds");
        }
        this.current = this.points[index];
        this.currentIndex = index;
        this.next = this.points[index + 1];
        this.hasNext = index + 1 < this.points.length;
        this.nextMaxValue = this.nextMaxes[index];
        return this.current;
    }
    advanceToMax() {
        for (let i = Math.max(0, this.currentIndex); i < this.points.length; i++) {
            if (this.points[i].value == this.nextMaxValue) {
                return this.advanceToIndex(i);
            }
        }
        return this.current;
    }
    copy() {
        return new FrontierPointIterator(this.points, this.currentIndex, this.current, this.next, this.hasNext, this.nextMaxes, this.nextMaxValue);
    }
    static create(points) {
        // start with first element as next
        const currentIndex = -1;
        const current = points[currentIndex];
        const next = points[currentIndex + 1];
        const hasNext = currentIndex + 1 < points.length;
        const nextMaxes = Array(points.length).fill(-Infinity);
        let max = -Infinity;
        let i = points.length;
        while (i--) {
            nextMaxes[i] = max;
            const value = points[i].value;
            if (value > max) {
                max = value;
            }
        }
        const nextMaxValue = nextMaxes[currentIndex];
        return new FrontierPointIterator(points, currentIndex, current, next, hasNext, nextMaxes, nextMaxValue);
    }
}
class SegmentOptimizer {
    config;
    mappers;
    iterator;
    constraintProvider;
    constructor(config, mappers, iterator, constraintProvider) {
        this.config = config;
        this.mappers = mappers;
        this.iterator = iterator;
        this.constraintProvider = constraintProvider;
    }
    optimizeWithoutGlobalConstraint() {
        if (this.config.assumptions.defaultIndex >= 0) {
            return this.config.assumptions.defaultIndex;
        }
        let maxIndex = 0;
        let max = this.getValue(maxIndex);
        for (let i = maxIndex + 1; i < this.iterator.points.length; i++) {
            if (this.isWithinBounds(i)) {
                const value = this.getValue(i);
                if (value > max) {
                    max = value;
                    maxIndex = i;
                }
            }
        }
        return maxIndex;
    }
    getSegmentId() {
        return this.config.segmentId;
    }
    getConfig() {
        return this.config;
    }
    getAssumptions() {
        return this.config.assumptions;
    }
    getPoint(index) {
        return this.iterator.points[index];
    }
    getConfusionMatrix(index) {
        return this.iterator.points[index].confusionMatrix;
    }
    getValue(index) {
        return this.iterator.points[index].value;
    }
    getMappedValues(index) {
        return this.mappers.map((mapper) => mapper(this.iterator.points[index].confusionMatrix));
    }
    isWithinBounds(index) {
        return this.constraintProvider.getConstraint(this.iterator.points[index]).isWithinBounds;
    }
    canIncrease(index, stopAtOptimal = false) {
        if (index >= this.iterator.maxIndex) {
            return false;
        }
        else if (stopAtOptimal) {
            // only proceed if there is a max ahead
            return this.iterator.nextMaxes[index] > this.iterator.points[index].value;
        }
        return true;
    }
    getInitialIndex() {
        for (let i = 0; i < this.iterator.points.length; i++) {
            if (this.isWithinBounds(i)) {
                return i;
            }
        }
        return 0;
    }
    static fromConfig(config, metric, fairnessStrategy) {
        const costMapper = (cm) => cm.getAllPositive() * config.assumptions.unitCost;
        const valueMapper = ObjectiveMetric.asMapper(metric, config.assumptions);
        const constraintProvider = SegmentConstraintProvider.createFromSegment(config);
        const points = config.confusionMatrices
            .filter((confusionMatrix) => confusionMatrix)
            .map((confusionMatrix) => FrontierPoint.create(confusionMatrix, config.model, costMapper, valueMapper));
        let filteredPoints;
        if (config.assumptions.defaultIndex >= 0) {
            /* The default index is the presumed optimal index based on a previous optimizer run.
                  This index is based on the unfiltered list of points. We filter the points to cut
                  down on the search space for the optimizer. This is not necessary when the defaultIndex
                  is specified. */
            filteredPoints = points;
        }
        else {
            filteredPoints = points.filter((point) => constraintProvider.getConstraint(point).isWithinBounds);
            if (filteredPoints.length == 0) {
                filteredPoints = [points[0]];
            }
        }
        const iterator = FrontierPointIterator.create(filteredPoints);
        const current = iterator.advance();
        return (current &&
            new SegmentOptimizer(config, FairnessStrategy.asMappers(fairnessStrategy, config.assumptions), iterator, constraintProvider));
    }
}
export var SegmentMetric;
(function (SegmentMetric) {
    SegmentMetric["Profit"] = "profit";
    SegmentMetric["Revenue"] = "revenue";
    SegmentMetric["Cost"] = "cost";
})(SegmentMetric || (SegmentMetric = {}));
(function (SegmentMetric) {
    function asFrontierPointAccessor(metric, assumptions) {
        switch (metric) {
            case SegmentMetric.Profit:
                return (point) => point.confusionMatrix.getImpact(assumptions.costBenefitMatrix);
            case SegmentMetric.Cost:
                return (point) => point.cost;
            case SegmentMetric.Revenue:
                return (point) => point.confusionMatrix.getTruePositive() * assumptions.unitRevenue;
        }
    }
    SegmentMetric.asFrontierPointAccessor = asFrontierPointAccessor;
})(SegmentMetric || (SegmentMetric = {}));
class ProjectOptimizerContext {
    segments;
    constraintProvider;
    constructor(segments, constraintProvider) {
        this.segments = segments;
        this.constraintProvider = constraintProvider;
    }
    getConstraint(pointIndices) {
        return this.constraintProvider.getConstraint([this.segments, pointIndices]);
    }
    getValue(candidate) {
        let value = 0;
        let i = this.segments.length;
        while (i--) {
            value += this.segments[i].getValue(candidate.pointIndices[i]);
        }
        return value;
    }
    optimizeWithoutGlobalConstraint() {
        return {
            pointIndices: this.segments.map((segment) => segment.optimizeWithoutGlobalConstraint()),
            activeSegmentIndex: 0,
            hasSeenInBounds: false
        };
    }
    segmentCanIncrease(pointIndices, segmentIndex, hasSeenInBounds) {
        return this.segments[segmentIndex].canIncrease(pointIndices[segmentIndex], hasSeenInBounds);
    }
    isWithinBounds(candidate) {
        if (!this.getConstraint(candidate.pointIndices).isWithinBounds) {
            return false;
        }
        let i = this.segments.length;
        while (i--) {
            if (!this.segments[i].isWithinBounds(candidate.pointIndices[i])) {
                return false;
            }
        }
        return true;
    }
    getInitialCandidate() {
        return {
            pointIndices: this.segments.map((s) => s.getInitialIndex()),
            activeSegmentIndex: 0,
            hasSeenInBounds: false
        };
    }
    static createInitial(segments, constraintProvider, fairnessStrategy = FairnessStrategy.None) {
        if (fairnessStrategy != FairnessStrategy.None) {
            return ProjectOptimizerContextWithFixedMetric.createInitial(segments, constraintProvider, fairnessStrategy);
        }
        return new ProjectOptimizerContext(segments, constraintProvider);
    }
}
class ProjectOptimizerContextWithFixedMetric extends ProjectOptimizerContext {
    evaluator;
    constructor(segments, constraintProvider, evaluator) {
        super(segments, constraintProvider);
        this.evaluator = evaluator;
    }
    isWithinBounds(candidate) {
        const values = candidate.pointIndices
            .map((pointIndex, segmentIndex) => this.segments[segmentIndex].getMappedValues(pointIndex))
            .map((x) => x.filter(isFinite))
            .filter((x) => x.length >= 0);
        return this.evaluator(values) && super.isWithinBounds(candidate);
    }
    static createInitial(segments, constraintProvider, fairnessStrategy = FairnessStrategy.None) {
        return new ProjectOptimizerContextWithFixedMetric(segments, constraintProvider, FairnessStrategy.asEvaluator(fairnessStrategy, segments.map((s) => s.getConfig())));
    }
}
export var FairnessStrategy;
(function (FairnessStrategy) {
    FairnessStrategy["None"] = "none";
    FairnessStrategy["TargetAllocation"] = "target_allocation";
    FairnessStrategy["EqualAccuracy"] = "equal_accuracy";
    FairnessStrategy["EqualTruePositiveRate"] = "equal_true_positive_rate";
    FairnessStrategy["EqualFalsePositiveRate"] = "equal_false_positive_rate";
    FairnessStrategy["EqualOdds"] = "equal_odds";
})(FairnessStrategy || (FairnessStrategy = {}));
(function (FairnessStrategy) {
    function asMappers(strategy, assumptions) {
        switch (strategy) {
            case FairnessStrategy.TargetAllocation:
                switch (assumptions.allocationMetric) {
                    case AllocationMetric.Profit:
                        return [(cm) => cm.getImpact(assumptions.costBenefitMatrix)];
                    case AllocationMetric.Cost:
                        return [(cm) => cm.getAllPositive() * assumptions.unitCost];
                    case AllocationMetric.Units:
                    default:
                        return [(cm) => cm.getAllPositive()];
                }
            case FairnessStrategy.EqualAccuracy:
                return [ClassificationMetric.toMapper(ClassificationMetric.Accuracy)];
            case FairnessStrategy.EqualTruePositiveRate:
                return [ClassificationMetric.toMapper(ClassificationMetric.TruePositiveRate)];
            case FairnessStrategy.EqualFalsePositiveRate:
                return [ClassificationMetric.toMapper(ClassificationMetric.FalsePositiveRate)];
            case FairnessStrategy.EqualOdds:
                return [
                    ClassificationMetric.toMapper(ClassificationMetric.TruePositiveRate),
                    ClassificationMetric.toMapper(ClassificationMetric.FalsePositiveRate)
                ];
            case FairnessStrategy.None:
            default:
                return [(cm) => 0];
        }
    }
    FairnessStrategy.asMappers = asMappers;
    function asEvaluator(strategy, segments) {
        switch (strategy) {
            case FairnessStrategy.TargetAllocation:
                const total = segments
                    .map((config) => config.model.confusionMatrixFactory.getConfusionMatrix().getTotal())
                    .reduce((x, y) => x + y, 0);
                const allocations = segments.map((segment) => segment.assumptions.targetAllocation !== undefined
                    ? segment.assumptions.targetAllocation
                    : segment.model.confusionMatrixFactory.getConfusionMatrix().getTotal() / total);
                return (valuesArr) => {
                    const values = valuesArr.map((x) => x[0]);
                    const total = values.reduce((x, y) => x + y, 0);
                    if (total == 0) {
                        return allocations.every((x) => x == 0);
                    }
                    for (let i = 0; i < values.length; i++) {
                        const fraction = values[i] / total;
                        if (Math.abs(fraction - allocations[i]) > 0.01) {
                            return false;
                        }
                    }
                    return true;
                };
            case FairnessStrategy.EqualOdds:
            case FairnessStrategy.EqualAccuracy:
            case FairnessStrategy.EqualTruePositiveRate:
            case FairnessStrategy.EqualFalsePositiveRate:
                let nMetrics = strategy == FairnessStrategy.EqualOdds ? 2 : 1;
                const boundType = segments?.[0]?.boundType ?? BoundType.Value;
                const configBound = segments
                    .map((s) => s.fairnessToleranceBound)
                    .filter((x) => x !== undefined)
                    .map(Math.abs)
                    .reduce((x, y) => Math.max(x, y), 0);
                const defaultBound = boundType == BoundType.Value ? 0.01 : 0.2;
                const bound = configBound === 0 ? defaultBound : configBound;
                return (valuesArr) => {
                    for (let i = 0; i < nMetrics; i++) {
                        const values = valuesArr.map((x) => x[i]);
                        const min = Math.min(...values);
                        const max = Math.max(...values);
                        const delta = max - min;
                        switch (boundType) {
                            case BoundType.Value:
                                if (delta >= bound) {
                                    return false;
                                }
                                break;
                            case BoundType.PercentLower:
                                if (min > 0 && delta / min >= bound) {
                                    return false;
                                }
                                break;
                            case BoundType.PercentHigher:
                                if (max > 0 && delta / max >= bound) {
                                    return false;
                                }
                                break;
                        }
                    }
                    return true;
                };
            case FairnessStrategy.None:
            default:
                return (values) => true;
        }
    }
    FairnessStrategy.asEvaluator = asEvaluator;
})(FairnessStrategy || (FairnessStrategy = {}));
export var ObjectiveMetric;
(function (ObjectiveMetric) {
    ObjectiveMetric["Profit"] = "profit";
    ObjectiveMetric["Revenue"] = "revenue";
    ObjectiveMetric["Cost"] = "cost";
})(ObjectiveMetric || (ObjectiveMetric = {}));
(function (ObjectiveMetric) {
    function asMapper(metric, assumptions) {
        switch (metric) {
            case ObjectiveMetric.Profit:
                return (cm) => cm.getImpact(assumptions.costBenefitMatrix);
            case ObjectiveMetric.Cost:
                return (cm) => -1 * cm.getAllPositive() * assumptions.unitCost;
            case ObjectiveMetric.Revenue:
                return (cm) => cm.getTruePositive() * assumptions.unitRevenue;
        }
    }
    ObjectiveMetric.asMapper = asMapper;
})(ObjectiveMetric || (ObjectiveMetric = {}));
class ProjectOptimizerContextProvider {
    segmentConfigs;
    metric;
    constraintConfigs;
    fairnessStrategy;
    constructor(segmentConfigs, metric, constraintConfigs, fairnessStrategy) {
        this.segmentConfigs = segmentConfigs;
        this.metric = metric;
        this.constraintConfigs = constraintConfigs;
        this.fairnessStrategy = fairnessStrategy;
    }
    createContext() {
        const segments = this.segmentConfigs
            .map((segment) => SegmentOptimizer.fromConfig(segment, this.metric, this.fairnessStrategy))
            .filter((segment) => segment);
        const constraintProvider = CompositeConstraintProvider.create(this.constraintConfigs.map((config) => ProjectConstraintProvider.create(config, segments)));
        return ProjectOptimizerContext.createInitial(segments, constraintProvider, this.fairnessStrategy);
    }
}
export class ProjectOptimizer {
    contextProvider;
    constructor(contextProvider) {
        this.contextProvider = contextProvider;
    }
    _getDefaultStepSize() {
        switch (this.contextProvider.segmentConfigs.length) {
            case 1:
            case 2:
            case 3:
                return 1; // theoretical max of 1000000
            case 4:
                return 2; // theoretical max of 6250000
            case 5:
                return 4; // theoretical max of 9765625
            default:
                return 5; // theoretical max of 64000000
        }
    }
    optimize() {
        const context = this.contextProvider.createContext();
        let optimal = context.optimizeWithoutGlobalConstraint();
        const converged = true;
        if (!context.isWithinBounds(optimal)) {
            const stepSize = this._getDefaultStepSize();
            let candidate = context.getInitialCandidate();
            let optimalValue = -Infinity;
            let candidateValue = -Infinity;
            let hasOptimal = false;
            let segmentIndex;
            let child;
            const candidates = [];
            while (candidate !== undefined) {
                if (context.isWithinBounds(candidate)) {
                    candidate.hasSeenInBounds = true;
                    candidateValue = context.getValue(candidate);
                    if (optimalValue < candidateValue) {
                        optimalValue = candidateValue;
                        optimal = candidate;
                        hasOptimal = true;
                    }
                }
                segmentIndex = candidate.activeSegmentIndex;
                while (segmentIndex < context.segments.length) {
                    if (context.segments[segmentIndex].canIncrease(candidate.pointIndices[segmentIndex], candidate.hasSeenInBounds)) {
                        child = {
                            pointIndices: [...candidate.pointIndices],
                            activeSegmentIndex: segmentIndex,
                            hasSeenInBounds: candidate.hasSeenInBounds
                        };
                        child.pointIndices[segmentIndex] += stepSize;
                        if (stepSize > 1 && child.pointIndices[segmentIndex] > context.segments[segmentIndex].iterator.maxIndex) {
                            child.pointIndices[segmentIndex] = context.segments[segmentIndex].iterator.maxIndex;
                        }
                        candidates.push(child);
                    }
                    segmentIndex++;
                }
                candidate = candidates.pop();
            }
            if (candidates.length == 0 && !hasOptimal && child != undefined) {
                optimal = child;
            }
        }
        return {
            segments: optimal.pointIndices.map((pointIndex, segmentIndex) => ({
                confusionMatrix: context.segments[segmentIndex].getPoint(pointIndex).confusionMatrix,
                segmentId: context.segments[segmentIndex].getConfig().segmentId
            })),
            isWithinBounds: context.isWithinBounds(optimal),
            converged: converged
        };
    }
    static create(segmentConfigs, metric = ObjectiveMetric.Profit, constraintConfigs, fairnessStrategy = FairnessStrategy.None) {
        const provider = new ProjectOptimizerContextProvider(segmentConfigs, metric, constraintConfigs, fairnessStrategy);
        return new ProjectOptimizer(provider);
    }
}
